import { useEffect, useMemo, useRef, useState } from 'react'
import type { Address, Hex, PublicClient, WalletClient } from 'viem'
import { encodeFunctionData, formatEther, getAddress, isAddress, keccak256, pad, toHex } from 'viem'
import { LSP0_ABI, PERMISSIONS, PERMISSION_CONTROL } from '../constants'
import { PERMISSION_CONTROL_ABI } from '../contracts/permissionControl'
import {
  AllowedCallsEditor,
  AllowedDataKeysEditor,
  PermissionSelector,
} from './index'
import {
  buildLsp4TemplateMetadataDocument,
  convertEntriesToAllowedCalls,
  convertAllowedCallsToEntries,
  convertAllowedDataKeysToEntries,
  decodeAllowedCalls,
  decodeAllowedDataKeys,
  encodeAllowedCalls,
  encodeAllowedDataKeys,
  formatAddress,
  pinJsonToIpfs,
  slugifyFromName,
  type AllowedCallEntry,
  type DataKeyEntry,
} from '../utils'
import type { ProfileData } from '../hooks/useWallet'

export interface PublisherTemplateFormProps {
  isConnected: boolean
  upAddress: Address | null
  chainId: number | null
  walletClient: WalletClient | null
  publicClient: PublicClient | null
  profileData: ProfileData | null
  onPublished: () => void
  onClose?: () => void
  initialValues?: PublisherTemplateFormInitialValues | null
}

export interface PublisherTemplateFormInitialValues {
  slug?: string
  version?: number
  permissions?: bigint
  allowedCallsHex?: Hex
  allowedDataKeysHex?: Hex
  templateName?: string
  appName?: string
  controllerAddress?: string
  templateDescription?: string
  appWebsite?: string
  docsUrl?: string
  brandColor?: string
}

export function PublisherTemplateForm({
  isConnected,
  upAddress,
  chainId,
  walletClient,
  publicClient,
  profileData,
  onPublished,
  onClose,
  initialValues,
}: PublisherTemplateFormProps) {
  const [slug, setSlug] = useState('')
  const [version, setVersion] = useState(1)
  const [permissions, setPermissions] = useState<bigint>(0n)
  const [allowedCallEntries, setAllowedCallEntries] = useState<AllowedCallEntry[]>([])
  const [allowedDataKeyEntries, setAllowedDataKeyEntries] = useState<DataKeyEntry[]>([])
  const [templateName, setTemplateName] = useState('')
  /** Name of the app creating this template — stored in LSP4 metadata as attribute `app`. */
  const [appName, setAppName] = useState('')
  /** Controller that will receive these permissions when someone installs the template — stored in LSP4 metadata as `controllerAddress`. */
  const [controllerAddressInput, setControllerAddressInput] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [appWebsite, setAppWebsite] = useState('')
  const [docsUrl, setDocsUrl] = useState('')
  const [brandColor, setBrandColor] = useState('')
  const [status, setStatus] = useState<'idle' | 'publishing' | 'success' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)
  /** On-chain mint fee (wei) from PermissionControl.mintPrice() */
  const [mintPriceWei, setMintPriceWei] = useState<bigint | null>(null)

  useEffect(() => {
    if (!publicClient) {
      setMintPriceWei(null)
      return
    }
    let cancelled = false
    void publicClient
      .readContract({
        address: PERMISSION_CONTROL.address,
        abi: PERMISSION_CONTROL_ABI,
        functionName: 'mintPrice',
      })
      .then((v) => {
        if (!cancelled) setMintPriceWei(v as bigint)
      })
      .catch(() => {
        if (!cancelled) setMintPriceWei(null)
      })
    return () => {
      cancelled = true
    }
  }, [publicClient])

  const templateId = useMemo(() => keccak256(toHex(slug)) as Hex, [slug])
  const didPrefillApp = useRef(false)
  const [autoSlugFromTemplateName, setAutoSlugFromTemplateName] = useState(true)

  useEffect(() => {
    if (didPrefillApp.current || !profileData?.profileName) return
    setAppName(profileData.profileName)
    didPrefillApp.current = true
  }, [profileData?.profileName])

  // Prepopulate slug from template name; user can still edit the slug field
  useEffect(() => {
    if (!autoSlugFromTemplateName) return
    const next = slugifyFromName(templateName)
    setSlug((prev) => (next ? next : prev))
  }, [templateName, autoSlugFromTemplateName])

  useEffect(() => {
    if (!initialValues) return
    setSlug(initialValues.slug?.trim() || '')
    setVersion(initialValues.version && initialValues.version > 0 ? initialValues.version : 1)
    setPermissions(initialValues.permissions ?? 0n)

    const calls = initialValues.allowedCallsHex ? decodeAllowedCalls(initialValues.allowedCallsHex) : []
    setAllowedCallEntries(convertAllowedCallsToEntries(calls))
    const keys = initialValues.allowedDataKeysHex ? decodeAllowedDataKeys(initialValues.allowedDataKeysHex) : []
    setAllowedDataKeyEntries(convertAllowedDataKeysToEntries(keys))

    setTemplateName(initialValues.templateName?.trim() || '')
    setAppName(initialValues.appName?.trim() || '')
    setControllerAddressInput(initialValues.controllerAddress?.trim() || '')
    setTemplateDescription(initialValues.templateDescription || '')
    setAppWebsite(initialValues.appWebsite || '')
    setDocsUrl(initialValues.docsUrl || '')
    setBrandColor(initialValues.brandColor || '')

    const hasExplicitSlug = (initialValues.slug?.trim().length ?? 0) > 0
    setAutoSlugFromTemplateName(!hasExplicitSlug)
    if (initialValues.appName?.trim()) {
      didPrefillApp.current = true
    }
  }, [initialValues])

  const controllerAddressValid = useMemo(() => {
    const c = controllerAddressInput.trim()
    return c.length > 0 && isAddress(c)
  }, [controllerAddressInput])

  const canPublish =
    isConnected &&
    !!upAddress &&
    !!walletClient &&
    (chainId ?? PERMISSION_CONTROL.chainId) === PERMISSION_CONTROL.chainId &&
    slug.trim().length > 0 &&
    version > 0 &&
    permissions > 0n &&
    appName.trim().length > 0 &&
    controllerAddressValid

  /**
   * LSP4-shaped document + optional Pinata `ipfs://…` (same pattern as Groov: `LSP4Metadata` + attributes).
   * Includes `permissions`, `allowedCalls`, `allowedERC725YDataKeys` attributes so IPFS JSON matches
   * what is registered on-chain (same bytes as `publishTemplate`).
   * Without `VITE_PINATA_JWT`, falls back to minified inline JSON (still `LSP4Metadata` wrapper).
   */
  async function resolveMetadataUriForPublish(params: {
    permissions: bigint
    allowedCallsBytes: Hex
    allowedDataKeysBytes: Hex
  }): Promise<string> {
    const displayName = templateName.trim() || `${appName.trim()} — Template`
    const normalizedBrandColor = /^#([0-9a-fA-F]{6})$/.test(brandColor.trim()) ? brandColor.trim() : undefined
    const permissionsHex = pad(toHex(params.permissions), { size: 32 })
    const doc = buildLsp4TemplateMetadataDocument({
      displayName,
      description: templateDescription || undefined,
      slug,
      version,
      chainId: PERMISSION_CONTROL.chainId,
      app: appName.trim(),
      controllerAddress: getAddress(controllerAddressInput.trim() as Address),
      publisherUP: upAddress ?? undefined,
      website: appWebsite || undefined,
      docsUrl: docsUrl || undefined,
      primaryColor: normalizedBrandColor,
      permissionsHex,
      allowedCallsHex: params.allowedCallsBytes !== '0x' ? params.allowedCallsBytes : undefined,
      allowedERC725YDataKeysHex: params.allowedDataKeysBytes !== '0x' ? params.allowedDataKeysBytes : undefined,
    })
    const jwt = import.meta.env.VITE_PINATA_JWT?.trim()
    if (jwt) {
      try {
        return await pinJsonToIpfs(doc, jwt)
      } catch (e) {
        console.warn('[publish] Pinata pin failed, using inline LSP4 JSON metadataURI', e)
      }
    }
    return JSON.stringify(doc)
  }

  /**
   * Single transaction: mint LSP8 template token on PermissionControl with metadata URI payload + mint fee.
   */
  const publish = async () => {
    if (!canPublish || !upAddress || !walletClient || !publicClient) return
    setError(null)
    setStatus('publishing')

    try {
      const mintPrice =
        mintPriceWei ??
        ((await publicClient.readContract({
          address: PERMISSION_CONTROL.address,
          abi: PERMISSION_CONTROL_ABI,
          functionName: 'mintPrice',
        })) as bigint)

      const allowedCalls = allowedCallEntries.length > 0 ? convertEntriesToAllowedCalls(allowedCallEntries) : []
      const allowedCallsBytes = allowedCalls.length > 0 ? encodeAllowedCalls(allowedCalls) : ('0x' as Hex)
      const allowedDataKeys = allowedDataKeyEntries.length > 0
        ? (allowedDataKeyEntries.map(e => e.key as Hex))
        : []
      const allowedDataKeysBytes = allowedDataKeys.length > 0 ? encodeAllowedDataKeys(allowedDataKeys) : ('0x' as Hex)

      const metadataURI = await resolveMetadataUriForPublish({
        permissions,
        allowedCallsBytes,
        allowedDataKeysBytes,
      })

      await publicClient.simulateContract({
        address: PERMISSION_CONTROL.address,
        abi: PERMISSION_CONTROL_ABI,
        functionName: 'publishTemplate',
        args: [
          templateId,
          version,
          BigInt(PERMISSION_CONTROL.chainId),
          permissions,
          allowedCallsBytes,
          allowedDataKeysBytes,
          metadataURI,
        ],
        account: upAddress,
        value: mintPrice,
      })

      const publishData = encodeFunctionData({
        abi: PERMISSION_CONTROL_ABI,
        functionName: 'publishTemplate',
        args: [
          templateId,
          version,
          BigInt(PERMISSION_CONTROL.chainId),
          permissions,
          allowedCallsBytes,
          allowedDataKeysBytes,
          metadataURI,
        ],
      })

      await walletClient.writeContract({
        address: upAddress,
        abi: LSP0_ABI,
        functionName: 'execute',
        args: [0n, PERMISSION_CONTROL.address, mintPrice, publishData],
        account: upAddress,
        chain: walletClient.chain,
      })

      setStatus('success')
      onPublished()
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to publish'
      const msg =
        raw.includes('PublisherMustBeUniversalProfile')
          ? 'Publish failed: this contract rejects your profile address as a valid Universal Profile implementation.'
          : raw.includes('IncorrectMintFee')
            ? 'Publish failed: mint fee does not match the contract (check mintPrice on PermissionControl).'
            : raw.includes('TemplateAlreadyExists')
              ? 'Publish failed: this template slug + version already exists for your UP.'
              : raw.includes('InvalidVersion')
                ? 'Publish failed: version must be greater than zero.'
                : raw.includes('execution reverted')
                  ? 'Publish reverted on-chain. Try a fresh slug/version, ensure your Universal Profile holds enough LYX for the mint fee + gas, and verify execute permission.'
                  : raw
      setError(msg)
      setStatus('error')
    }
  }

  const isPublishing = status === 'publishing'

  // Show Allowed Calls only when a permission that uses call restrictions is set
  const permissionsUsingCalls =
    BigInt(PERMISSIONS.CALL) |
    BigInt(PERMISSIONS.SUPER_CALL) |
    BigInt(PERMISSIONS.STATICCALL) |
    BigInt(PERMISSIONS.SUPER_STATICCALL) |
    BigInt(PERMISSIONS.DELEGATECALL) |
    BigInt(PERMISSIONS.SUPER_DELEGATECALL) |
    BigInt(PERMISSIONS.TRANSFERVALUE) |
    BigInt(PERMISSIONS.SUPER_TRANSFERVALUE) |
    BigInt(PERMISSIONS.EXECUTE_RELAY_CALL)
  const showAllowedCalls = (permissions & permissionsUsingCalls) !== 0n

  // Show Allowed Data Keys only when SETDATA or SUPER_SETDATA is set
  const permissionsUsingDataKeys = BigInt(PERMISSIONS.SETDATA) | BigInt(PERMISSIONS.SUPER_SETDATA)
  const showAllowedDataKeys = (permissions & permissionsUsingDataKeys) !== 0n

  return (
    <div className="space-y-4">
      <div className="card-hub">
        <div className="space-y-4">
          <div>
            <div className="text-sm font-semibold text-gray-200">Template metadata</div>
            <div className="text-xs text-gray-500 mt-1">
              Stored as LSP4 metadata (LSP4Metadata JSON). With <span className="font-mono">VITE_PINATA_JWT</span> set, the JSON is pinned to IPFS and <span className="font-mono">metadataURI</span> is <span className="font-mono">ipfs://…</span>; otherwise a compact JSON string is written on-chain.
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Template name</label>
              <input
                className="input input-subtle"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Forever Moments — Gasless interactions"
              />
            </div>
            <div>
              <label className="label">App name</label>
              <input
                className="input input-subtle"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="Forever Moments"
              />
              <p className="mt-1 text-xs text-gray-500">
                Name of the app publishing this template. Stored in metadata as <span className="font-mono">app</span>.
              </p>
            </div>
          </div>

          <div>
            <label className="label">Controller address</label>
            <input
              className="input input-subtle font-mono"
              value={controllerAddressInput}
              onChange={(e) => setControllerAddressInput(e.target.value)}
              placeholder="0x… — address that will receive these permissions when someone installs"
            />
            <p className="mt-1 text-xs text-gray-500">
              Required. Installers use this to know which controller to add. Stored in LSP4 metadata as{' '}
              <span className="font-mono">controllerAddress</span>.
            </p>
            {controllerAddressInput.trim().length > 0 && !controllerAddressValid && (
              <p className="mt-1 text-xs text-red-400">Enter a valid Ethereum address (0x + 40 hex chars).</p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label">Description (optional)</label>
              <textarea
                className="input input-subtle min-h-[90px]"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Explain what this enables and what it cannot do."
              />
            </div>
            <div>
              <label className="label">App website (optional)</label>
              <input
                className="input input-subtle"
                value={appWebsite}
                onChange={(e) => setAppWebsite(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="label">Docs URL (optional)</label>
              <input
                className="input input-subtle"
                value={docsUrl}
                onChange={(e) => setDocsUrl(e.target.value)}
                placeholder="https://docs..."
              />
            </div>
            <div>
              <label className="label">Brand color (optional)</label>
              <input
                className="input input-subtle font-mono"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                placeholder="#7C3AED"
              />
              <p className="mt-1 text-xs text-gray-500">
                Use a 6-digit hex value (example: <span className="font-mono">#7C3AED</span>).
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Template slug</label>
              <input
                className="input input-subtle font-mono"
                value={slug}
                onChange={(e) => {
                  setAutoSlugFromTemplateName(false)
                  setSlug(e.target.value)
                }}
                placeholder="forever-moments/gasless"
              />
              <p className="mt-2 text-xs text-gray-500">
                On-chain identifier, derived from the name above. Edit if you want a different one (e.g. <span className="font-mono">app-name/feature</span>).
              </p>
            </div>
            <div>
              <label className="label">Version</label>
              <input
                className="input input-subtle"
                type="number"
                min={1}
                value={version}
                onChange={(e) => setVersion(parseInt(e.target.value || '1', 10))}
              />
              <p className="mt-2 text-xs text-gray-500">
                Version (1, 2, …) for publishing updates. Chain: {PERMISSION_CONTROL.chainId} (LUKSO mainnet).
              </p>
            </div>
          </div>
        </div>
      </div>

      <PermissionSelector
        value={permissions}
        onChange={(p) => setPermissions(p)}
        hidePresets={true}
        defaultMode="custom"
      />

      {showAllowedCalls && (
        <AllowedCallsEditor
          entries={allowedCallEntries}
          onChange={setAllowedCallEntries}
          chainId={chainId ?? PERMISSION_CONTROL.chainId}
        />
      )}
      {showAllowedDataKeys && (
        <AllowedDataKeysEditor entries={allowedDataKeyEntries} onChange={setAllowedDataKeyEntries} />
      )}

      <div className="card-hub">
        <p className="mb-3 text-xs text-gray-500">
          One transaction: you sign from your connected Universal Profile to mint the template token on PermissionControl. Metadata is sent in that call and stored with the token. The mint fee is paid from your UP’s LYX balance (see below), plus gas.
        </p>
        <div className="mb-3 rounded-xl border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-sm">
          <span className="text-gray-400">Mint fee (on-chain): </span>
          {mintPriceWei !== null ? (
            <span className="font-mono text-gray-200"> {formatEther(mintPriceWei)} LYX</span>
          ) : (
            <span className="text-gray-500">Loading…</span>
          )}
        </div>
        {error && (
          <div className="mb-3 p-3 rounded-xl border border-red-500/30 bg-red-500/10 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="btn-primary"
              disabled={!canPublish || !publicClient || isPublishing}
              onClick={publish}
            >
              {status === 'publishing' ? 'Minting template…' : 'Publish template'}
            </button>
            {onClose && (
              <button type="button" className="btn-secondary" onClick={onClose} disabled={isPublishing}>
                Cancel
              </button>
            )}
          </div>
          <span className="text-xs text-gray-500">
            Publisher (connected UP): {upAddress ? formatAddress(upAddress, 8) : 'not connected'}
          </span>
        </div>
      </div>
    </div>
  )
}

